import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const initialState = {
  brands: [],
  getBrandsLoading: false,
  getBrandsError: null,
  createBrandLoading: false,
  createBrandError: null,
  updateBrandLoading: false,
  updateBrandError: null,
  selectedBrand: null,
  viewBrandLoading: false,
  viewBrandError: null,
  deleteBrandLoading: false,
  deleteBrandError: null,
};

export const fetchBrands = createAsyncThunk(
  "brands/fetchBrands",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("OrviSlideToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/brands`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to fetch brands",
        );
      }
      const data = await response.json();
      const brandData = data.data;
      return brandData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createBrand = createAsyncThunk(
  "brands/createBrand",
  async (brandData, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/brands`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: brandData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData);
      }
      const data = await response.json();

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateBrand = createAsyncThunk(
  "brands/updateBrand",
  async ({ brandData, brandId }, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/brands/${brandId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: brandData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData);
      }
      const data = await response.json();

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const viewBrand = createAsyncThunk(
  "brands/viewBrand",
  async (brandId, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/brands/${brandId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to view brand",
        );
      }
      const data = await response.json();
      const brandData = data.data;
      return brandData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteBrand = createAsyncThunk(
  "brands/deleteBrand",
  async (brandId, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/brands/${brandId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to delete brand",
        );
      }
      const isNoContent = response.status === 204;
      const data = isNoContent ? { id: brandId } : await response.json();
      return data.id || brandId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const brandsSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchBrands.pending, (state) => {
        state.getBrandsLoading = true;
        state.getBrandsError = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.getBrandsLoading = false;
        state.brands = action.payload;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.getBrandsLoading = false;
        state.getBrandsError = action.payload;
      })
      // create
      .addCase(createBrand.pending, (state) => {
        state.createBrandLoading = true;
      })
      .addCase(createBrand.fulfilled, (state, action) => {
        state.createBrandLoading = false;
        state.brands.push(action.payload);
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.createBrandLoading = false;
        state.createBrandError = action.payload;
      })
      // ✅ Update
      .addCase(updateBrand.pending, (state) => {
        state.updateBrandLoading = true;
        state.updateBrandError = null;
      })
      .addCase(updateBrand.fulfilled, (state, action) => {
        state.updateBrandLoading = false;
        const updated = action.payload;
        state.brands = state.brands.map((brand) =>
          brand.id === updated.id ? updated : brand,
        );
      })
      .addCase(updateBrand.rejected, (state, action) => {
        state.updateBrandLoading = false;
        state.updateBrandError = action.payload;
      })
      //   view
      .addCase(viewBrand.pending, (state) => {
        state.viewBrandLoading = true;
        state.viewBrandError = null;
      })
      .addCase(viewBrand.fulfilled, (state, action) => {
        state.viewBrandLoading = false;
        state.selectedBrand = action.payload;
      })
      .addCase(viewBrand.rejected, (state, action) => {
        state.viewBrandLoading = false;
        state.viewBrandError = action.payload;
      })
      //  Delete
      .addCase(deleteBrand.pending, (state) => {
        state.deleteBrandLoading = true;
        state.deleteBrandError = null;
      })
      .addCase(deleteBrand.fulfilled, (state, action) => {
        state.deleteBrandLoading = false;
        state.brands = state.brands.filter((b) => b.id !== action.payload);
      })
      .addCase(deleteBrand.rejected, (state, action) => {
        state.deleteBrandLoading = false;
        state.deleteBrandError = action.payload;
      });
  },
});

export default brandsSlice.reducer;
